/**
 * The "these answers are not real" markers.
 *
 * When the backend has no `AZURE_SEARCH_ENDPOINT` the retriever falls back to a
 * seeded in-memory corpus. That corpus is deliberately convincing — realistic
 * Norican IT prose, plausible ClickLearn URLs, plausible "Updated" dates — and
 * it renders through exactly the same citation cards as the real index. A user
 * has no way to tell the difference, and fabricated IT guidance that looks
 * sourced is the most dangerous thing this app can put on a screen.
 *
 * So there are two markers, not one:
 *
 * - {@link DemoCorpusNotice} goes *above* the answers, where it is read before
 *   the answer is, rather than as a footnote under one.
 * - {@link DemoCorpusBadge} goes on each individual source card, because a
 *   screenshot of one card, out of context and without the banner, is exactly
 *   how this misleads.
 *
 * Both render `null` when the real index is configured — no banner, no badge,
 * no reserved space, no layout shift. The flag comes from the server
 * (`getCapabilities`), which reads it off the retriever object it actually
 * built, so the UI cannot claim the index is real while the fake answers.
 */

import { Icon, TriangleAlert } from "@norican/icons";
import { Badge, Button, cn, Popover, Typography } from "@norican/react";

import { useT } from "@/i18n";
import { useCapabilities } from "@/lib/api";

/**
 * True when knowledge answers on this deployment come from the demo corpus.
 *
 * Unresolved and failed capability lookups both read as `false`: the banner is
 * for a known-fake corpus, and a page that flashes "this is fake" while it
 * waits for a request would be its own kind of wrong.
 */
export function useDemoKnowledgeCorpus(): boolean {
  const { data } = useCapabilities();
  return data?.demo_knowledge_corpus === true;
}

export interface DemoCorpusNoticeProps {
  className?: string;
}

/**
 * The prototype banner above any surface that shows knowledge answers.
 *
 * This was a full `Alert` card carrying four sentences, which on the home page
 * put a block of amber between the headline and the composer — the warning won
 * the page from the thing the page is for. It is now one line, with the detail
 * a click away.
 *
 * The wording is not softened, and the trigger is a real button rather than a
 * hover tooltip, because the detail has to be reachable by keyboard and on
 * touch. What changed is how much room it takes to say it, not whether it is
 * said: it still renders nothing at all once a real index is configured.
 */
export function DemoCorpusNotice({ className }: DemoCorpusNoticeProps) {
  const { t } = useT("assistant");
  const isDemo = useDemoKnowledgeCorpus();

  if (!isDemo) {
    return null;
  }

  return (
    <div
      role="status"
      className={cn(
        // Lighter than the full warning surface: the border carries the amber
        // and the fill is a wash of it, so the line reads as a standing
        // condition rather than an alert firing. Text stays on
        // `--norican-warning-fg`, which is the token paired with this surface
        // for contrast — the notice is quieter, not fainter.
        "border-warning-border/60 bg-warning-subtle/50 flex flex-wrap items-center gap-x-2 gap-y-0.5 rounded-md border px-3 py-1.5",
        className,
      )}
    >
      <Icon icon={TriangleAlert} size="sm" className="text-warning-fg shrink-0" />
      <Typography as="span" variant="caption" className="text-warning-fg min-w-0 flex-1">
        {t("knowledge.demoCorpus.short")}
      </Typography>

      <Popover.Root>
        <Popover.Trigger
          render={
            <Button variant="ghost" tone="neutral" size="sm" className="text-warning-fg shrink-0">
              {t("knowledge.demoCorpus.learnMore")}
            </Button>
          }
        />
        <Popover.Portal>
          <Popover.Positioner side="bottom" align="end">
            <Popover.Popup className="max-w-[var(--norican-size-container-sm)]">
              <Popover.Header>
                <Popover.Title>{t("knowledge.demoCorpus.title")}</Popover.Title>
                <Popover.CloseButton />
              </Popover.Header>
              <Popover.Body>
                <Popover.Description>{t("knowledge.demoCorpus.body")}</Popover.Description>
              </Popover.Body>
              <Popover.Arrow />
            </Popover.Popup>
          </Popover.Positioner>
        </Popover.Portal>
      </Popover.Root>
    </div>
  );
}

export interface DemoCorpusBadgeProps {
  className?: string;
}

/**
 * The per-card marker, sitting beside the source badge it qualifies.
 *
 * Solid rather than subtle: it has to survive being cropped into a screenshot
 * next to a real-looking ClickLearn chip, and a faint outline does not.
 */
export function DemoCorpusBadge({ className }: DemoCorpusBadgeProps) {
  const { t } = useT("assistant");
  const isDemo = useDemoKnowledgeCorpus();

  if (!isDemo) {
    return null;
  }

  return (
    <Badge tone="warning" variant="solid" size="sm" className={cn(className)}>
      {t("knowledge.demoCorpus.badge")}
    </Badge>
  );
}
